import { useState } from "react";
import QRCode from "qrcode";
import Loader from "react-loader-spinner";
import LazyLoad from "react-lazy-load";
import { connect } from "react-redux";
import { pdf } from "@react-pdf/renderer";
import JsBarcode from "jsbarcode";
import { toastr } from "react-redux-toastr";
import { saveAs } from "file-saver";
import classNames from "classnames";
import { InstructionLabel, BrandingLabel } from "sticker_templates";
import {
  Tooltip,
  QrCodeStickerModal,
  LocalizationInfo,
  QrCodeModal,
  ClipboardCopy,
} from "components";
import { Label, ModalPreview } from "expano-components";
import { setGrnLineMissing } from "actions/grns";
import { getPackageBoxById } from "utils/package_boxes";

import {
  CapacityModal,
  GrnLineBatches,
  SupplierBatchNumber,
} from "../../../../../components";

import { ReactComponent as QRcodeIcon } from "icons/qrcode.svg";
import { ReactComponent as Sticker } from "icons/sticker.svg";
import { ReactComponent as PreviewIcon } from "icons/preview.svg";
import { ReactComponent as WarningIcon } from "icons/light-warning.svg";

const ItemRow = ({ grn_identifier, data, setGrnLineMissing }) => {
  const [is_modal_open, openModal] = useState(false);
  const [is_generating_instruction_labels, setGeneratingInstructionLabels] =
    useState(false);
  const [is_generating_branding_labels, setGeneratingBrandingLabels] =
    useState(false);
  const [is_loading, setLoading] = useState(false);
  const [is_qr_codes_modal_open, openQrCodesModal] = useState(false);
  const [
    capacity_product_main_location_data,
    setCapacityProductMainLocationData,
  ] = useState(null);
  const [is_open_grn_qr_modal, setOpenGrnQrModal] = useState(false);

  const main_location = data?.product?.product_main_locations?.find((item) =>
    [1].includes(item.warehouse?.id),
  );

  const shipping_location = data?.product?.product_main_locations?.find(
    (item) => [2].includes(item.warehouse?.id),
  );
  const image_url = data.product?.image_url;

  const is_virtual_shipment_position =
    shipping_location?.warehouse_position?.id === 3034; // P02A01-R01P01M01

  const main_grn_line_batches = data.grn_line_batches.filter(
    ({ warehouse_position, deleted_warehouse_position_warehouse_id }) =>
      (deleted_warehouse_position_warehouse_id ||
        warehouse_position?.warehouse?.id) === 1,
  );

  const handleSetMissing = async () => {
    try {
      setLoading(true);
      await setGrnLineMissing(data.id);
    } finally {
      setLoading(false);
    }
  };

  const generateInstructionLabels = async () => {
    try {
      setGeneratingInstructionLabels(true);
      const errors = [];

      if (!grn_identifier) {
        errors.push("Brak identyfikatora dostawy.");
      }

      if (grn_identifier && !grn_identifier.startsWith("ZD/")) {
        errors.push("Identyfikator powinien rozpoczynać się od ZD/");
      }

      if (!data.product?.name) {
        errors.push("Brak nazwy produktu.");
      }

      if (!data.product?.name_en) {
        errors.push("Brak tłumaczenia EN.");
      }

      if (!data.product?.name_de) {
        errors.push("Brak tłumaczenia DE.");
      }

      if (!data.product?.producer_code) {
        errors.push("Brak kodu producenta.");
      }

      if (!data.product?.ean) {
        errors.push("Brak Ean.");
      }

      if (
        data?.requires_supplier_batch_number_on_delivery &&
        !data.supplier_batch_number
      ) {
        errors.push("Brak numeru partii dostawcy.");
      }

      if (errors.length > 0) {
        throw new Error(errors.join(" "));
      }

      const canvas = document.createElement("canvas");
      JsBarcode(canvas, data.product.ean, {
        format: "EAN13",
        width: 2,
        height: 60,
        displayValue: true,
      });

      const barcode = canvas.toDataURL("image/png");
      const qr_code = data.product.instruction_url
        ? await QRCode.toDataURL(data.product.instruction_url)
        : null;

      const blob = await pdf(
        <InstructionLabel
          data={{
            name: data.product.name,
            name_en: data.product.name_en,
            name_de: data.product.name_de,
            producer_code: data.product.producer_code,
            part_number: grn_identifier,
            supplier_part_number: data.supplier_batch_number,
            quantity: data?.quantity || 1,
            barcode,
            qr_code,
          }}
        />,
      ).toBlob();
      saveAs(blob, `${data?.product?.sku}_etykieta.pdf`);
    } catch (error) {
      toastr.error("Błąd", error?.message || "Wystąpił błąd");
    } finally {
      setGeneratingInstructionLabels(false);
    }
  };

  const generateBrandingLabels = async () => {
    try {
      setGeneratingBrandingLabels(true);

      const blob = await pdf(
        <BrandingLabel
          data={{
            quantity: data?.quantity || 1,
            branding_label_url: data.product.branding_label_url,
          }}
        />,
      ).toBlob();
      saveAs(blob, `${data?.product?.sku}_branding.pdf`);
    } catch (error) {
      toastr.error("Błąd", error?.message || "Wystąpił błąd");
    } finally {
      setGeneratingBrandingLabels(false);
    }
  };

  const branding_package_box = getPackageBoxById(
    data?.product?.branding_pack_box,
  );

  return (
    <tr
      data-focus-id={data.id}
      className={classNames(data?.counting_state, {
        is_warned: data?.is_warned,
        is_decomposed:
          data?.supplier_quantity?.is_decomposed ||
          data?.supplier_quantity?.is_composed,
        no_location: !data.has_location_in_shipment_warehouse,
        deleted: data.is_deleted,
      })}
      style={{
        display: data?.show ? "table-row" : "none",
      }}
    >
      <td className="number">
        <span>{data?.no}</span>
      </td>
      <td className="image-box">
        {image_url && (
          <LazyLoad>
            <button
              tabIndex="-1"
              className="modal__trigger"
              onClick={() => openModal(true)}
              style={{
                backgroundImage: `url('${
                  data?.product?.image_thumb_url || image_url
                }')`,
              }}
            />
          </LazyLoad>
        )}
      </td>
      <td>
        <p>
          <a
            className="text-link"
            href={`/products/${data?.product?.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {data?.product?.name}
          </a>
        </p>
        <p className="text-info">SKU: {data?.product?.sku}</p>
        <p className="text-info">
          Kod dostawcy:{" "}
          {data?.supplier_code ? (
            <ClipboardCopy text={data?.supplier_code} />
          ) : (
            "-"
          )}
        </p>
        <p className="text-info">
          Wymiary produktu:{" "}
          {data?.product?.package_width
            ? parseFloat(data.product.package_width).toFixed(2)
            : "brak"}
          x{" "}
          {data?.product?.package_height
            ? parseFloat(data.product.package_height).toFixed(2)
            : "brak"}{" "}
          x{" "}
          {data?.product?.package_depth
            ? parseFloat(data.product.package_depth).toFixed(2)
            : "brak"}{" "}
          cm
        </p>
        <p className="text-info">
          Objętość produktu:{" "}
          {data?.product?.volume
            ? parseFloat(data.product.volume).toFixed(2)
            : "0.00"}{" "}
          cm<sup>3</sup>
        </p>
        {!data.has_location_in_shipment_warehouse && (
          <Label
            type="fill"
            color="#fd397a"
            text="Brak lokalizacji na magazynie wysyłkowym"
          />
        )}
        <SupplierBatchNumber data={data} />
      </td>
      <td>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 5,
          }}
        >
          {data?.product?.need_instruction_label && (
            <button
              type="button"
              disabled={is_generating_instruction_labels}
              onClick={generateInstructionLabels}
              tabIndex="-1"
            >
              {is_generating_instruction_labels ? (
                <Loader type="Oval" color="#567cfb" height={15} width={15} />
              ) : (
                "ℹ️"
              )}
            </button>
          )}
          {data?.product?.need_branding_label && (
            <button
              type="button"
              disabled={is_generating_branding_labels}
              onClick={generateBrandingLabels}
              tabIndex="-1"
            >
              {is_generating_branding_labels ? (
                <Loader type="Oval" color="#567cfb" height={15} width={15} />
              ) : (
                "🏷️"
              )}
            </button>
          )}
          {data?.product?.need_branding_pack && (
            <>
              <span>📦 {branding_package_box?.name}</span>
              <span style={{ fontSize: 12 }}>
                {branding_package_box.sizes?.width}x
                {branding_package_box.sizes?.height}x
                {branding_package_box.sizes?.depth}
              </span>
            </>
          )}
          {data?.product?.need_custom_brand_label && (
            <Tooltip
              trigger={<span>🎫</span>}
              content={
                <div>
                  <p>{data.product.brand_label?.name || "Brak etykiety markowej"}</p>
                  {(data.product.brand_label?.image_thumb_url ||
                    data.product.brand_label?.image_url) && (
                    <img
                      src={
                        data.product.brand_label.image_thumb_url ||
                        data.product.brand_label.image_url
                      }
                      alt={data.product.brand_label?.name}
                      style={{ display: "block", maxWidth: 180, marginTop: 8 }}
                    />
                  )}
                </div>
              }
            />
          )}
        </div>
      </td>
      <td>
        <div className="codes-group">
          <div className="codes-group__box">
            <button
              className="m-r-5"
              type="button"
              onClick={() => setOpenGrnQrModal(true)}
              tabIndex="-1"
            >
              <PreviewIcon />
            </button>
            {data.product?.has_ean && <Sticker />}
            {data.product?.sku && data?.product?.has_qr_code && (
              <button
                type="button"
                onClick={() => openQrCodesModal(true)}
                tabIndex="-1"
              >
                <QRcodeIcon />
              </button>
            )}
          </div>
        </div>
      </td>
      <td className="box-count bl text-center">
        {data?.supplier_quantity?.is_decomposed ||
        data?.supplier_quantity?.is_composed ? (
          <>
            <p>
              <strong>
                {data.supplier_quantity.package_qty}{" "}
                {data.supplier_quantity?.supplier_custom_unit || "op."}
              </strong>
            </p>
            {data.supplier_quantity.unit_qty > 0 ? (
              <p>
                <strong>{data.supplier_quantity.unit_qty} szt.</strong>
              </p>
            ) : null}
            <p className="text-muted">{data?.quantity} szt.</p>
          </>
        ) : (
          <>
            <p>
              <strong>{data.quantity}</strong>
            </p>
            <p>{data?.unit}</p>
          </>
        )}
      </td>
      {is_virtual_shipment_position ? (
        <>
          <td className="bl">
            <div className="warehouse-form">
              <div className="warehouse-form__box">
                <Label type="fill" color="#567cfb" text="Gabarytowa" />
                {main_location?.warehouse_position ? (
                  <LocalizationInfo
                    show_tooltip={false}
                    main={main_location?.warehouse_position}
                  />
                ) : (
                  <p>brak</p>
                )}
                <p className="suggested-count m-r-5">
                  Na lok.: {main_location?.stock} szt.
                </p>
                <p
                  className={classNames("suggested-count pointer", {
                    warning: main_location?.is_final_capacity,
                  })}
                  onClick={() =>
                    setCapacityProductMainLocationData({
                      id: main_location.id,
                      capacity: main_location.capacity,
                      is_final_capacity: main_location.is_final_capacity,
                    })
                  }
                >
                  Pojemność: {main_location?.capacity} szt.
                </p>
              </div>
              <div className="warehouse-form__box">
                <GrnLineBatches
                  grn_line_id={data.id}
                  data={main_grn_line_batches}
                />
              </div>
            </div>
          </td>
        </>
      ) : (
        <td className="bl">
          {!main_location ? (
            "-"
          ) : (
            <div className="warehouse-form">
              <div className="warehouse-form__box">
                <LocalizationInfo
                  show_tooltip={false}
                  main={main_location?.warehouse_position}
                />
                <p className="suggested-count">
                  Na lok.: {main_location?.stock} szt
                </p>
                <p
                  className={classNames("suggested-count pointer", {
                    warning: main_location?.is_final_capacity,
                  })}
                  onClick={() =>
                    setCapacityProductMainLocationData({
                      id: main_location.id,
                      capacity: main_location.capacity,
                      is_final_capacity: main_location.is_final_capacity,
                    })
                  }
                >
                  Pojemność: {main_location?.capacity} szt.
                </p>
              </div>
              <div className="warehouse-form__box">
                <GrnLineBatches
                  grn_line_id={data.id}
                  data={main_grn_line_batches}
                />
              </div>
            </div>
          )}
        </td>
      )}
      <td className="bl text-center">
        {data?.counted_quantity !== null ? (
          <strong>
            {data.counted_quantity} {data?.unit || "szt."}
          </strong>
        ) : (
          <button
            disabled={is_loading}
            type="button"
            className="btn btn-sm btn-danger"
            onClick={handleSetMissing}
          >
            Brak
          </button>
        )}
      </td>
      <td className="bl text-center">
        {data?.points_to_earn
          ? parseFloat(data.points_to_earn).toFixed(2)
          : "0.00"}{" "}
        pkt.
      </td>
      <td className="bl text-center">
        {data?.is_warned && (
          <Tooltip trigger={<WarningIcon />} content={<p>Sprawdź produkt</p>} />
        )}
        {data?.product?.is_warehouseman_alert_enabled &&
          data?.product?.warehouseman_alert && (
            <Tooltip content={<p>{data.product.warehouseman_alert}</p>} />
          )}
      </td>
      {is_modal_open && (
        <ModalPreview
          handleClose={() => {
            openModal(false);
          }}
          src={image_url}
          alt={data?.product?.name}
        />
      )}
      {is_qr_codes_modal_open && (
        <QrCodeStickerModal
          data={data.product}
          handleClose={() => openQrCodesModal(false)}
          initialValues={{
            quantity: data?.quantity || 1,
          }}
        />
      )}
      {capacity_product_main_location_data?.id && (
        <CapacityModal
          grn_line_id={data.id}
          product_main_location_id={capacity_product_main_location_data?.id}
          initialValues={{
            capacity: capacity_product_main_location_data?.capacity || null,
            is_final_capacity:
              capacity_product_main_location_data?.is_final_capacity || false,
          }}
          handleClose={() => setCapacityProductMainLocationData(null)}
        />
      )}
      {is_open_grn_qr_modal && (
        <QrCodeModal
          identifier={data.product.sku}
          handleClose={() => setOpenGrnQrModal(false)}
          title="Roznieś produkt"
          value={`grn_line_id|${data.id}`}
        />
      )}
    </tr>
  );
};

export default connect(null, (dispatch) => ({
  setGrnLineMissing: (id) => dispatch(setGrnLineMissing(id)),
}))(ItemRow);
